import { useCallback, useRef } from 'react';

// Phần tử tương tác: bấm vào đây thì không bắt đầu kéo
const INTERACTIVE_SELECTOR =
  'input, textarea, select, button, a, label, [role="button"], .MuiCheckbox-root, .cursor-pointer';

// Kéo quá ngưỡng này (px) mới tính là kéo, nhỏ hơn vẫn là click bình thường
const DRAG_THRESHOLD = 5;

/**
 * Cho phép giữ chuột trái vào vùng trống của phần tử rồi kéo để cuộn ngang/dọc,
 * thay vì phải kéo thanh scroll.
 */
export const useDragScroll = () => {
  const cleanupRef = useRef(null);

  // Callback ref: tự gắn/gỡ sự kiện khi phần tử mount/unmount (kể cả render có điều kiện)
  return useCallback(el => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    if (!el) return;

    let startX = 0;
    let startY = 0;
    let startScrollLeft = 0;
    let startScrollTop = 0;
    let isPointerDown = false;
    let isDragging = false;

    const onMouseMove = e => {
      if (!isPointerDown) return;
      const dx = e.clientX - startX;
      const dy = e.clientY - startY;

      if (!isDragging) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        isDragging = true;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
        window.getSelection()?.removeAllRanges();
      }

      el.scrollLeft = startScrollLeft - dx;
      el.scrollTop = startScrollTop - dy;
    };

    const onMouseUp = () => {
      isPointerDown = false;
      el.style.cursor = '';
      el.style.userSelect = '';
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };

    const onMouseDown = e => {
      // Xoá cờ của lần kéo trước (vd thả chuột ngoài bảng nên không có click)
      isDragging = false;
      if (e.button !== 0 || e.target.closest(INTERACTIVE_SELECTOR)) return;
      isPointerDown = true;
      startX = e.clientX;
      startY = e.clientY;
      startScrollLeft = el.scrollLeft;
      startScrollTop = el.scrollTop;
      window.addEventListener('mousemove', onMouseMove);
      window.addEventListener('mouseup', onMouseUp);
    };

    // Vừa kéo xong thì chặn click để không kích hoạt hành động của dòng
    const onClickCapture = e => {
      if (!isDragging) return;
      e.stopPropagation();
      e.preventDefault();
      isDragging = false;
    };

    el.addEventListener('mousedown', onMouseDown);
    el.addEventListener('click', onClickCapture, true);

    cleanupRef.current = () => {
      el.removeEventListener('mousedown', onMouseDown);
      el.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);
};

export default useDragScroll;
